/**
 * 对话面板组件
 *
 * 显示选中用户的对话历史 + 底部回复输入区
 * 支持 SSE 实时追加新消息、自动滚动到底部
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  fetchConversationHistory,
  sendCustomMessage,
  type ConversationMessage,
  type ActiveUser,
} from '../hooks/useWechatApi';
import type { SseNewMessage } from '../hooks/useWechatSse';
import { MessageBubble } from './MessageBubble';
import { ReplyInput } from './ReplyInput';
import { IconMessages as MessageSquare } from '@tabler/icons-react';

interface ConversationPanelProps {
  selectedUser: ActiveUser | null;
  sseIncomingMessage: SseNewMessage | null;
  onSseMessageConsumed: () => void;
}

/** 获取显示名称 */
function getDisplayName(user: ActiveUser): string {
  if (user.person?.fullName) return user.person.fullName;
  if (user.user?.nickname) return user.user.nickname;
  if (user.user?.username) return user.user.username;
  return user.openid.substring(0, 8) + '...';
}

export function ConversationPanel({
  selectedUser,
  sseIncomingMessage,
  onSseMessageConsumed,
}: ConversationPanelProps) {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pageSize = 50;

  // 滚动到底部
  const scrollToBottom = useCallback((smooth = true) => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: smooth ? 'smooth' : 'instant',
      });
    }, 50);
  }, []);

  // 加载对话历史
  const loadHistory = useCallback(
    async (openid: string, p: number, append = false) => {
      setLoading(true);
      const result = await fetchConversationHistory(openid, {
        page: p,
        pageSize,
      });
      // 后端按 createdAt desc 排序，前端反转为正序
      const sorted = [...result.items].reverse();
      if (append) {
        setMessages((prev) => [...sorted, ...prev]);
      } else {
        setMessages(sorted);
      }
      setHasMore(result.hasNext);
      setPage(p);
      setLoading(false);
    },
    [pageSize]
  );

  // 选中用户变化 → 重新加载
  useEffect(() => {
    if (selectedUser) {
      setMessages([]);
      setPage(1);
      void loadHistory(selectedUser.openid, 1).then(() => {
        scrollToBottom(false);
      });
    } else {
      setMessages([]);
    }
  }, [selectedUser?.openid]); // eslint-disable-line react-hooks/exhaustive-deps

  // SSE 收到当前用户的新消息 → 追加
  useEffect(() => {
    if (sseIncomingMessage && selectedUser && sseIncomingMessage.openid === selectedUser.openid) {
      // 创建一个临时消息对象追加到列表
      const tempMessage: ConversationMessage = {
        id: `sse-${Date.now()}`,
        openid: sseIncomingMessage.openid,
        userId: null,
        direction: 'in',
        msgType: sseIncomingMessage.msgType,
        content: sseIncomingMessage.content,
        operatorId: null,
        status: 'sent',
        readStatus: 'read',
        errorCode: null,
        errorMessage: null,
        createdAt: sseIncomingMessage.timestamp,
        user: selectedUser.user,
        person: selectedUser.person,
        operator: null,
      };
      setMessages((prev) => [...prev, tempMessage]);
      scrollToBottom();
      onSseMessageConsumed();
    }
  }, [sseIncomingMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  // 加载更多历史（上滑）
  const handleLoadMore = useCallback(async () => {
    if (!selectedUser || !hasMore || loading) return;
    const scrollContainer = scrollContainerRef.current;
    const prevScrollHeight = scrollContainer?.scrollHeight ?? 0;

    await loadHistory(selectedUser.openid, page + 1, true);

    // 保持滚动位置
    if (scrollContainer) {
      const newScrollHeight = scrollContainer.scrollHeight;
      scrollContainer.scrollTop = newScrollHeight - prevScrollHeight;
    }
  }, [selectedUser, hasMore, loading, page, loadHistory]);

  // 发送消息
  const handleSend = useCallback(
    async (content: string) => {
      if (!selectedUser || !content.trim()) return;
      setSending(true);
      try {
        await sendCustomMessage(selectedUser.openid, 'text', content);
        // 追加发送的消息到列表
        const sentMessage: ConversationMessage = {
          id: `sent-${Date.now()}`,
          openid: selectedUser.openid,
          userId: null,
          direction: 'out',
          msgType: 'text',
          content,
          operatorId: null,
          status: 'sent',
          readStatus: 'read',
          errorCode: null,
          errorMessage: null,
          createdAt: new Date().toISOString(),
          user: null,
          person: null,
          operator: null, // 当前用户信息可从 Redux 获取，暂简化处理
        };
        setMessages((prev) => [...prev, sentMessage]);
        scrollToBottom();
      } finally {
        setSending(false);
      }
    },
    [selectedUser, scrollToBottom]
  );

  // 检查窗口是否过期
  const isWindowExpired = selectedUser
    ? new Date(selectedUser.expiresAt).getTime() < Date.now()
    : false;

  if (!selectedUser) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <MessageSquare className="size-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">选择一个用户查看对话</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* 头部：用户名称 */}
      <div className="h-12 px-4 flex items-center border-b border-divider shrink-0">
        <span className="text-sm font-medium">{getDisplayName(selectedUser)}</span>
      </div>

      {/* 消息历史区域 */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
      >
        {/* 加载更多 */}
        {hasMore && (
          <div className="text-center py-2">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="text-xs text-primary hover:underline disabled:opacity-50"
            >
              {loading ? '加载中...' : '加载更多历史消息'}
            </button>
          </div>
        )}

        {/* 消息列表 */}
        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            senderUser={selectedUser}
          />
        ))}

        {/* 加载占位 */}
        {loading && messages.length === 0 && (
          <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
            加载中...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 回复输入区 */}
      <ReplyInput
        onSend={handleSend}
        disabled={isWindowExpired}
        sending={sending}
        expiredMessage={isWindowExpired ? '48 小时互动窗口已关闭，无法发送消息' : undefined}
      />
    </div>
  );
}
